export class LoadingOverlayService {
    private static instance: LoadingOverlayService;
    private activeOverlays = new Set<HTMLElement>();

    private constructor() {}

    static getInstance(): LoadingOverlayService {
        if (!LoadingOverlayService.instance) {
            LoadingOverlayService.instance = new LoadingOverlayService();
        }
        return LoadingOverlayService.instance;
    }

    /**
     * Block the UI with a loading overlay
     * @param options Configuration options for the overlay
     */
    blockUI(options: {
        message?: string;
        overlayCSS?: Partial<CSSStyleDeclaration>;
        messageCSS?: Partial<CSSStyleDeclaration>;
        target?: HTMLElement;
    } = {}): void {
        const target = options.target || document.body;
        
        // Check if already blocked
        if (this.activeOverlays.has(target)) {
            return;
        }

        const overlay = this.createOverlay(options);
        target.appendChild(overlay);
        this.activeOverlays.add(target);

        // Force reflow to ensure smooth opacity transition
        overlay.offsetHeight;
        overlay.style.opacity = '1';
    }

    /**
     * Remove the UI block overlay
     * @param target The target element to unblock (defaults to document.body)
     */
    unblockUI(target?: HTMLElement): void {
        const targetElement = target || document.body;
        const overlay = targetElement.querySelector('.loading-overlay') as HTMLElement;
        
        if (overlay) {
            overlay.style.opacity = '0';
            setTimeout(() => {
                if (overlay.parentNode) {
                    overlay.parentNode.removeChild(overlay);
                }
                this.activeOverlays.delete(targetElement);
            }, 200); // Match CSS transition duration
        }
    }

    /**
     * Check if a target is currently blocked
     * @param target The target element to check
     */
    isBlocked(target?: HTMLElement): boolean {
        const targetElement = target || document.body;
        return this.activeOverlays.has(targetElement);
    }

    private createOverlay(options: {
        message?: string;
        overlayCSS?: Partial<CSSStyleDeclaration>;
        messageCSS?: Partial<CSSStyleDeclaration>;
    }): HTMLElement {
        const overlay = document.createElement('div');
        overlay.className = 'loading-overlay';
        
        // Default overlay styles
        const defaultOverlayStyles: Partial<CSSStyleDeclaration> = {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: '9999',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: '0',
            transition: 'opacity 0.2s ease-in-out'
        };

        // Apply styles
        Object.assign(overlay.style, defaultOverlayStyles, options.overlayCSS || {});

        // Create message container if message provided
        if (options.message) {
            const messageContainer = document.createElement('div');
            messageContainer.className = 'loading-message';
            messageContainer.textContent = options.message;

            // Default message styles
            const defaultMessageStyles: Partial<CSSStyleDeclaration> = {
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '5px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                fontSize: '16px',
                color: '#333',
                textAlign: 'center'
            };

            Object.assign(messageContainer.style, defaultMessageStyles, options.messageCSS || {});
            overlay.appendChild(messageContainer);
        } else {
            // Default spinner
            const spinner = this.createSpinner();
            overlay.appendChild(spinner);
        }

        return overlay;
    }

    private createSpinner(): HTMLElement {
        const spinner = document.createElement('div');
        spinner.className = 'spinner-border text-light';
        spinner.setAttribute('role', 'status');
        
        // Add screen reader text
        const srText = document.createElement('span');
        srText.className = 'visually-hidden';
        srText.textContent = 'Loading...';
        spinner.appendChild(srText);

        return spinner;
    }
}
